import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { API } from '@app-core/constants/api.constants';
import { DataService } from '@app-core/services/data/data.service';
import { getClientConfig } from '@config/client-config';
import { Router } from '@angular/router';
import { AccessService } from '@app-core/services/access/access.service';
import { LoginInfo } from '@app-core/models/core.model';
import { AmplifyAuthService } from './amplify-auth/amplify-auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  public loader = false;
  private defaultRedirectUrl = 'home';

  private tokenRefreshTimer;
  public clientConfig = getClientConfig().configuration;
  public env = getClientConfig().environments;

  constructor(
    private http: HttpClient,
    private dataService: DataService,
    private router: Router,
    private accessService: AccessService,
    private amplifyAuth: AmplifyAuthService
  ) {}

  public intermediateServerLogin(params: any, headersData?: any): Observable<LoginInfo> {
    const awsCognitoRegion = this.accessService.getAwsCognitoRegion();
    let headers = {};
    if (headersData.token) {
      headers = new HttpHeaders({ Authorization: `Bearer ${headersData.token}` });
    }
    return this.http.post<LoginInfo>(API.INTERMEDIATE_SERVER_LOGIN, { ...params, region: awsCognitoRegion }, { headers });
  }

  public intermediateServerMasterLoginAs(params: any): Observable<LoginInfo> {
    return this.http.post<LoginInfo>(API.INTERMEDIATE_SERVER_MASTER_LOGIN_AS, params);
  }

  public intermediateServerAdminLoginAs(params: any): Observable<LoginInfo> {
    return this.http.post<LoginInfo>(API.INTERMEDIATE_SERVER_ADMIN_LOGIN_AS, params);
  }
  private isValidAbsoluteUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return !!parsed.hostname; // Valid URL with hostname, not checking specific protocol to support companion app
    } catch {
      return false;
    }
  }

  public async logout(redirectUrl?: string | null) {
    if (this.env.isGeotabEnv) return;

    // Notify companion app before any web logout/navigation happens
    const w = window as any;

    const isAndroidCompanion = typeof w?.Android?.nativeLogout === 'function';

    const isIOSCompanion = typeof w?.webkit?.messageHandlers?.nativeLogout?.postMessage === 'function';

    if (isAndroidCompanion) {
      console.log('[Companion Logout] Android nativeLogout detected');

      try {
        w.Android.nativeLogout('logout');
        console.log('[Companion Logout] Android logout message sent');
      } catch (error) {
        console.error('[Companion Logout] Android logout failed', error);
      }

      this.dataService.userLogout.next();
      return;
    }

    if (isIOSCompanion) {
      console.log('[Companion Logout] iOS nativeLogout detected');

      try {
        w.webkit.messageHandlers.nativeLogout.postMessage('logout');
        console.log('[Companion Logout] iOS logout message sent');
      } catch (error) {
        console.error('[Companion Logout] iOS logout failed', error);
      }

      this.dataService.userLogout.next();
      return;
    }
    // Existing logout flow
    if (this.clientConfig.showCustomLoginPage) {
      await this.amplifyAuth.logout();
    }

    this.dataService.userLogout.next();

    if (redirectUrl && this.isValidAbsoluteUrl(redirectUrl)) {
      window.location.href = redirectUrl;
      return;
    }

    if (this.env.ssoAuthParams && this.env.redirect_url) {
      window.location.href = this.env.redirect_url;
    } else {
      this.redirectToLandingPage(true);
    }
  }

  private constructQueryParam(query: { [key: string]: any }) {
    return Object.entries(query)
      .filter(([, value]) => typeof value !== 'undefined')
      .map(([key, value]) => {
        if (Array.isArray(value) && value.length) {
          return value
            .filter((v) => typeof v !== 'undefined')
            .map((v) => `${key}[]=${v}`)
            .join('&');
        }
        return `${key}=${value}`;
      })
      .reduce((a, b) => `${a}&${b}`);
  }

  private async getAuthorizationParams(logout = false) {
    const awsCognitoRegion = this.accessService.getAwsCognitoRegion();
    const { domain, clientID } = this.env.cognitoConfigs[awsCognitoRegion];
    if (logout && this.clientConfig.showLandingPage) {
      return {
        domain,
        client_id: clientID,
        logout_uri: this.env.callbackURL.replace('/callback', ''),
      };
    }
    return {
      client_id: clientID,
      domain,
      response_type: 'code',
      scope: (this.env.scopes || []).join('+'),
      redirect_uri: this.env.callbackURL,
    };
  }
  public async authorizeUser() {
    if (this.clientConfig.showCustomLoginPage) {
      this.router.navigate(['/custom-login']);
    } else {
      const { domain, ...authorizationParams } = await this.getAuthorizationParams();
      window.location.href = `${API.oauth2Authorization(domain)}?${this.constructQueryParam(authorizationParams)}`;
    }
  }

  public async logoutUser() {
    if (this.clientConfig.showCustomLoginPage) {
      const callbackURL = this.env.callbackURL.replace('/callback', '');
      this.router.navigate([callbackURL]);
    } else {
      const { domain, ...authorizationParams } = await this.getAuthorizationParams(true);
      window.location.href = `${API.oauth2Logout(domain)}?${this.constructQueryParam(authorizationParams)}`;
    }
  }

  public async redirectToLandingPage(logout = false) {
    if (logout) {
      this.logoutUser();
    } else if (this.clientConfig.showLandingPage && !this.clientConfig.showCustomLoginPage) {
      this.router.navigate(['/login']);
    } else {
      this.authorizeUser();
    }
  }

  public redirectLoggedInUser(redirectUrl = this.defaultRedirectUrl) {
    this.router.navigateByUrl(redirectUrl);
  }

  scheduleAccessTokenRefresh(expiresAtInMs: number): void {
    const now = Date.now();
    const refreshBuffer = 60 * 1000; // refresh 1 minute early
    const timeout = expiresAtInMs - now - refreshBuffer;

    // clear existing timer
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
    }

    if (timeout > 0) {
      this.tokenRefreshTimer = setTimeout(() => {
        this.refreshAccessToken();
      }, timeout);
    } else {
      // timer expired
      this.refreshAccessToken();
    }
  }

  refreshAccessToken(): Promise<any> {
    const awsCognitoRegion = this.accessService.getAwsCognitoRegion();

    const loginInfo = this.accessService.getLoginInfo();

    // user navigated to fleet portal via admin portal, do not schedule refresh token
    if (loginInfo.loginType == 'admin') {
      return Promise.resolve(true);
    }

    return new Promise((res) => {
      this.http.post<any>(API.refreshAccessToken, { region: awsCognitoRegion }).subscribe(
        (response) => {
          const { token, expiresAtInMs } = response;

          // scheduling token refresh timer
          this.scheduleAccessTokenRefresh(expiresAtInMs);

          // setting new access token in local storage
          loginInfo.token = token;
          loginInfo.tokenExpiresAt = expiresAtInMs;

          this.accessService.setLoginInfo(loginInfo);

          res(true);
        },
        (err) => {
          res(false);
          console.log(err);
          this.logout();
        }
      );
    });
  }

  public validateRedirection(token: any): Observable<LoginInfo> {
    const redirectionToken = token;
    return this.http.post<LoginInfo>(API.VALIDATE_REDIRECTION(redirectionToken), {});
  }
}
